'use client'
import { Map, useMapsLibrary, useMap, AdvancedMarker } from '@vis.gl/react-google-maps'
import type { Marker } from '@googlemaps/markerclusterer'
import SearchClinics from './searchClinics'
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { useEffect, useRef, useState } from 'react'
import { WhereAreWePageFetchedData } from '@/Types/FetchedDataTypes'
import AddressCard from './addressCard'
import slugGenerator from '@/utils/slugGenerator'
interface ComponentProps {
  clinics: WhereAreWePageFetchedData[]
}

export default function MapComponent({ clinics }: ComponentProps) {
  const [clinicList, setClinicList] = useState(clinics) // Client Side Datas, datas coming from Directus
  const [searchedClinics, setSearchedClinics] = useState([]) // Client Side Datas, datas coming from Directus

  const [focusedClinic, setFocusedClinic] = useState<undefined | { lat: number, lng: number }>(undefined) // Google APIs
  const geocodingLib = useMapsLibrary('geocoding') // Google APIs
  const [zoom, setZoom] = useState<number | null>(6) // Google APIs
  const [defaultFocus, setDefaultFocus] = useState<{ lat: number, lng: number } | null>({ lat: 39.925533, lng: 32.866287 }) // Google APIs
  const MAP_ID = process.env.NEXT_PUBLIC_MAP_ID // Google APIs

  useEffect(() => { // Gets the cordinations
    if (!geocodingLib) return;

    const geocoder = new geocodingLib.Geocoder()
    clinics.forEach((clinic, i) => {
      geocoder.geocode({ address: clinic.hospitalName }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          setClinicList(prev => {
            const updatedList = [...prev]
            updatedList[i].location = { lat: loc.lat(), lng: loc.lng() }
            updatedList[i].slug = slugGenerator(updatedList[i])
            return updatedList
          })
        } else {
          console.warn(`Geocode failed for ${clinic.hospitalName}: ${status}`)
        }
      })
    })

  }, [geocodingLib, clinics])

  function resetValues() {
    setFocusedClinic(undefined)
    setDefaultFocus(null)
    setZoom(null)
  }


  return (
    <div className="flex w-full flex-col items-start justify-start lg:max-h-[none] p-5 lg:p-10 bg-[#F7F7F7]">
      <SearchClinics
        setSearchedClinics={setSearchedClinics}
        clinicList={clinicList}
      />
      <div className="flex flex-col lg:flex-row w-full items-start justify-start min-h-[50vh] space-y-5 lg:space-y-0 lg:space-x-3">
        {/* If there is searched clinic, it will re-render by keeping the old data */}
        <div className="w-full lg:w-1/2 max-h-[50vh] overflow-y-auto">
          {searchedClinics.length <= 0 ? clinicList.map((clinic: WhereAreWePageFetchedData) =>
            <AddressCard
              key={clinic.id}
              clinic={clinic}
              setFocusedClinic={setFocusedClinic}
              focusedClinic={focusedClinic}
              setZoom={setZoom}
            />
          ) : searchedClinics.map((clinic: WhereAreWePageFetchedData) =>
            <AddressCard
              key={clinic.id}
              clinic={clinic}
              setFocusedClinic={setFocusedClinic}
              focusedClinic={focusedClinic}
              setZoom={setZoom}
            />
          )}

        </div>

        <div className="w-full lg:flex-1 h-[60vh] sm:h-[110vh] lg:h-[50vh]">
          <Map
            mapId={MAP_ID}
            center={focusedClinic ? focusedClinic : defaultFocus}
            zoom={zoom}
            onMouseover={() => resetValues()}
          >
            <Markers clinicList={clinicList} />
          </Map>
        </div>
      </div>
    </div>
  )
}

const Markers = ({ clinicList }: any) => {

  const map = useMap()
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({})
  const clusterer = useRef<MarkerClusterer | null>(null)

  useEffect(() => {
    if (!map) return
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({ map })
    }
  }, [map])

  useEffect(() => {
    clusterer.current?.clearMarkers()
    clusterer.current?.addMarkers(Object.values(markers))
  }, [markers])

  function setMarkerRef(marker: Marker | null, key: string) {
    if (marker && markers[key]) return
    if (!marker && !markers[key]) return

    if (marker) {
      setMarkers(prev => ({ ...prev, [key]: marker }))
    } else {
      setMarkers(prev => {
        const newMarkers = { ...prev }
        delete newMarkers[key]
        return newMarkers
      })
    }
  }

  return (
    <>
      {clinicList.length > 0 && clinicList.map((clinic: WhereAreWePageFetchedData) =>
        <AdvancedMarker position={clinic.location} ref={(marker) => setMarkerRef(marker, `${clinic.id}`)} key={clinic.id} />
      )}
    </>
  )
}

